import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { closeConnectionModal, fetchAllPublicKeys } from '../../integrationsSlice'
import { fetchAllSecrets } from '../../../../features/aws/awsSecretsManagerSlice'
import { baseUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { linkFromChannel } from '../../../../modules/account'

const serviceAccountUrl = `${baseUrl}/auth/bigquery/service-account`

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
}

export default function BigQueryConnectionModalContent({ ownerId, retrieveAccounts }: Props) {
  const dispatch = useDispatch<any>()
  const closeModal = () => dispatch((closeConnectionModal as any)())
  const [jsonText, setJsonText] = useState('')
  const [impersonateUser, setImpersonateUser] = useState('')
  const [connectError, setConnectError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmitServiceAccount = async (event: React.FormEvent) => {
    event.preventDefault()
    setConnectError(null)
    setIsSubmitting(true)
    try {
      await axios.post(
        serviceAccountUrl,
        {
          service_account_json: jsonText.trim(),
          impersonate_user: impersonateUser.trim() || undefined,
        },
        getAxiosDefaultConfig()
      )
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      closeModal()
    } catch (err: any) {
      setConnectError(
        err?.response?.data?.error ||
          'Failed to connect the service account. Check the JSON key and BigQuery IAM on the GCP project.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-4">Connect Google BigQuery</h2>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-5">
        BigQuery uses a dedicated OAuth client, isolated from Analytics and Search Console. A GCP
        service-account key goes further by using JWT bearer tokens that are not tied to any user
        session.
      </p>

      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-mb-5">
        <p className="tw-font-semibold tw-mb-2">Google user (OAuth)</p>
        <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
          Uses a dedicated BigQuery OAuth grant. Google Workspace Cloud session control can still
          require this BigQuery user to reauthenticate, but it cannot take GA4 or Search Console
          down with it.
        </p>
        <Button
          onClick={() => {
            closeModal()
            linkFromChannel('bigquery')
          }}
        >
          Connect with Google
        </Button>
      </div>

      <form onSubmit={onSubmitServiceAccount} className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
        <p className="tw-font-semibold tw-mb-2">GCP service account</p>
        <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
          Paste a service-account JSON key with{' '}
          <code className="tw-text-xs">bigquery.readonly</code> (or broader BigQuery) on the project.
          For domain-wide delegation, also grant the SA permission to impersonate a Workspace user.
          See{' '}
          <a
            href="https://cloud.google.com/iam/docs/keys-create-delete"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 tw-underline"
          >
            Google Cloud service-account keys
            <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
          </a>
          .
        </p>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          Service account JSON
        </label>
        <textarea
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          required
          rows={8}
          spellCheck={false}
          placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN PRIVATE KEY-----\\n...","client_email":"...@....iam.gserviceaccount.com"}'
          className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-xs tw-font-mono tw-mb-3"
        />
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          Impersonate user (optional)
        </label>
        <input
          type="email"
          value={impersonateUser}
          onChange={(e) => setImpersonateUser(e.target.value)}
          placeholder="user@your-domain.com"
          className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm tw-mb-3"
        />
        {connectError && (
          <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-3">
            {connectError}
          </div>
        )}
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button variant="secondary" type="button" onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !jsonText.trim()}>
            {isSubmitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
                Connecting…
              </>
            ) : (
              'Save service account'
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
